import React, { useState, useEffect, useCallback } from 'react';
import { Modal } from 'react-bootstrap';
import swal from 'sweetalert';
import SeoService from '../../../services/SeoService';
import { PageHeader, Card, Button, Badge, Field, Input, Select, Toggle, Table, Tr, Td, EmptyState, inputClass } from '../../ui';

const TABS = [
  ['general', 'General', 'fa-gear'],
  ['social', 'Social & Org', 'fa-share-nodes'],
  ['analytics', 'Verify & Analytics', 'fa-chart-simple'],
  ['ai', 'AI Engines (GEO/AEO)', 'fa-robot'],
  ['pages', 'Pages', 'fa-file-lines'],
  ['faqs', 'FAQs', 'fa-circle-question'],
];

const CRAWLERS = [
  ['GPTBot', 'OpenAI — training'],
  ['OAI-SearchBot', 'OpenAI — ChatGPT Search'],
  ['ChatGPT-User', 'OpenAI — user browsing'],
  ['ClaudeBot', 'Anthropic — training'],
  ['anthropic-ai', 'Anthropic — legacy'],
  ['Claude-Web', 'Anthropic — Claude browsing'],
  ['PerplexityBot', 'Perplexity'],
  ['Google-Extended', 'Google — Gemini / Vertex'],
  ['Applebot-Extended', 'Apple Intelligence'],
  ['Amazonbot', 'Amazon — Alexa / Rufus'],
  ['CCBot', 'Common Crawl'],
  ['Bytespider', 'ByteDance / TikTok'],
];

const Textarea = ({ className = '', rows = 4, ...rest }) => (
  <textarea rows={rows} className={`${inputClass} ${className}`} {...rest} />
);

const Section = ({ title, children }) => (
  <Card title={title} className="tw-mb-5"><div className="tw-space-y-4">{children}</div></Card>
);

const emptyPage = {
  path: '', title: '', description: '', keywords: '', og_title: '', og_description: '',
  og_image: '', canonical: '', robots: 'index,follow', json_ld: '', ai_summary: '',
  changefreq: 'weekly', priority: 0.5, in_sitemap: 1, is_active: 1,
};
const emptyFaq = { question: '', answer: '', page_path: '', sort_order: 0, is_active: 1 };

const SeoSettings = () => {
  const [tab, setTab] = useState('general');
  const [cfg, setCfg] = useState(null);
  const [pages, setPages] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const [pageModal, setPageModal] = useState(null);
  const [faqModal, setFaqModal] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, p, f] = await Promise.all([
        SeoService.getAdminConfig(),
        SeoService.getPages().catch(() => []),
        SeoService.getAdminFaqs().catch(() => []),
      ]);
      setCfg(c); setPages(p); setFaqs(f);
    } catch (e) {
      setMsg({ ok: false, text: 'Failed to load SEO settings' });
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const flash = (ok, text) => { setMsg({ ok, text }); setTimeout(() => setMsg(null), 4000); };

  const set = (path, value) => {
    setCfg((prev) => {
      const next = JSON.parse(JSON.stringify(prev));
      const keys = path.split('.');
      let o = next;
      for (let i = 0; i < keys.length - 1; i++) o = o[keys[i]];
      o[keys[keys.length - 1]] = value;
      return next;
    });
  };

  const saveConfig = async () => {
    setSaving(true);
    try {
      const saved = await SeoService.updateConfig(cfg);
      setCfg(saved);
      flash(true, 'Saved. Live immediately — search & AI crawlers pick it up on their next visit.');
    } catch (e) {
      flash(false, e.response?.data?.error || 'Save failed');
    } finally { setSaving(false); }
  };

  const onOgUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      await SeoService.uploadOgImage(file);
      set('default_og_image', `${window.location.origin.replace('3000', '5000')}/api/seo/og-image`);
      set('og_image_uploaded', 1);
      flash(true, 'OG image uploaded.');
    } catch { flash(false, 'Upload failed'); }
  };

  const savePage = async () => {
    try {
      await SeoService.savePage(pageModal);
      setPageModal(null);
      setPages(await SeoService.getPages());
      flash(true, 'Route metadata saved.');
    } catch (e) { flash(false, e.response?.data?.error || 'Save failed'); }
  };
  const removePage = (row) => swal({ title: 'Delete override?', text: row.path, icon: 'warning', buttons: ['Cancel', 'Delete'], dangerMode: true })
    .then(async (ok) => { if (!ok) return; await SeoService.deletePage(row.id); setPages(await SeoService.getPages()); });

  const saveFaq = async () => {
    try {
      await SeoService.saveFaq(faqModal);
      setFaqModal(null);
      setFaqs(await SeoService.getAdminFaqs());
      flash(true, 'FAQ saved.');
    } catch (e) { flash(false, e.response?.data?.error || 'Save failed'); }
  };
  const removeFaq = (row) => swal({ title: 'Delete FAQ?', text: row.question, icon: 'warning', buttons: ['Cancel', 'Delete'], dangerMode: true })
    .then(async (ok) => { if (!ok) return; await SeoService.deleteFaq(row.id); setFaqs(await SeoService.getAdminFaqs()); });

  if (loading || !cfg) {
    return <div className="tw-py-16 tw-text-center"><i className="fa-solid fa-spinner fa-spin tw-text-2xl tw-text-brand" /></div>;
  }

  const showConfigSave = ['general', 'social', 'analytics', 'ai'].includes(tab);

  return (
    <div className="tw-mx-auto tw-max-w-4xl">
      <PageHeader
        title="SEO, GEO & AEO"
        subtitle="Search-engine, generative-engine and answer-engine metadata — managed here, served to the front-end via /api/seo/meta"
        actions={
          <>
            <Button variant="secondary" icon="fa-arrow-up-right-from-square" as="a" href={`${window.location.origin}/sitemap.xml`} target="_blank" rel="noreferrer">sitemap.xml</Button>
            <Button variant="secondary" icon="fa-robot" as="a" href={`${window.location.origin}/llms.txt`} target="_blank" rel="noreferrer">llms.txt</Button>
            {showConfigSave && <Button icon="fa-floppy-disk" onClick={saveConfig} disabled={saving}>{saving ? 'Saving…' : 'Save'}</Button>}
          </>
        }
      />

      {msg && (
        <div className={`tw-mb-5 tw-rounded-xl tw-border tw-px-4 tw-py-3 tw-text-sm ${msg.ok ? 'tw-border-good/30 tw-bg-good/10 tw-text-good' : 'tw-border-live/30 tw-bg-live/10 tw-text-live'}`}>{msg.text}</div>
      )}

      <div className="tw-mb-6 tw-flex tw-flex-wrap tw-gap-1.5">
        {TABS.map(([id, label, icon]) => (
          <button key={id} onClick={() => setTab(id)}
            className={`tw-rounded-lg tw-px-3 tw-py-1.5 tw-text-sm tw-font-medium ${tab === id ? 'tw-bg-brand tw-text-white' : 'tw-bg-sunken tw-text-content-muted hover:tw-text-content'}`}>
            <i className={`fa-solid ${icon} tw-mr-1.5`} />{label}
          </button>
        ))}
      </div>

      {tab === 'general' && (
        <>
          <Section title="Identity">
            <div className="tw-grid tw-gap-4 sm:tw-grid-cols-2">
              <Field label="Site name"><Input value={cfg.site_name} onChange={(e) => set('site_name', e.target.value)} /></Field>
              <Field label="Title template" hint="%s is the page title. e.g. “%s | Teve”"><Input value={cfg.title_template} onChange={(e) => set('title_template', e.target.value)} /></Field>
            </div>
            <Field label="Default title" hint="Used when a route has no override"><Input value={cfg.default_title} onChange={(e) => set('default_title', e.target.value)} /></Field>
            <Field label="Default description"><Textarea value={cfg.default_description} onChange={(e) => set('default_description', e.target.value)} /></Field>
            <Field label="Default keywords" hint="Comma-separated"><Textarea rows={2} value={cfg.default_keywords} onChange={(e) => set('default_keywords', e.target.value)} /></Field>
            <div className="tw-grid tw-gap-4 sm:tw-grid-cols-3">
              <Field label="Canonical base URL"><Input value={cfg.canonical_base_url} onChange={(e) => set('canonical_base_url', e.target.value)} /></Field>
              <Field label="Locale"><Input value={cfg.locale} onChange={(e) => set('locale', e.target.value)} placeholder="en_US" /></Field>
              <Field label="Theme color"><Input value={cfg.theme_color} onChange={(e) => set('theme_color', e.target.value)} /></Field>
            </div>
          </Section>
          <Section title="Sitemap & robots">
            <div className="tw-flex tw-items-center tw-justify-between">
              <div><div className="tw-font-semibold tw-text-heading">Publish sitemap.xml</div><div className="tw-text-sm tw-text-content-muted">Built from the Pages tab; linked in robots.txt</div></div>
              <Toggle checked={!!cfg.sitemap_enabled} onChange={(v) => set('sitemap_enabled', v ? 1 : 0)} />
            </div>
            <Field label="Extra robots.txt lines" hint="Appended verbatim (e.g. Disallow: /admin)"><Textarea value={cfg.robots_txt_extra} onChange={(e) => set('robots_txt_extra', e.target.value)} placeholder={'Disallow: /admin\nDisallow: /stream-dashboard'} /></Field>
          </Section>
        </>
      )}

      {tab === 'social' && (
        <>
          <Section title="Open Graph / Twitter">
            <Field label="Default social image (OG)" hint="1200×630 recommended. Absolute URL, or upload below.">
              <Input value={cfg.default_og_image} onChange={(e) => set('default_og_image', e.target.value)} />
            </Field>
            <div className="tw-flex tw-items-center tw-gap-3">
              <label className="tw-cursor-pointer tw-rounded-lg tw-border tw-border-line tw-bg-sunken tw-px-3 tw-py-2 tw-text-sm">
                <i className="fa-solid fa-upload tw-mr-2" />Upload image
                <input type="file" accept="image/*" hidden onChange={onOgUpload} />
              </label>
              {cfg.og_image_uploaded ? <Badge tone="good">custom image stored</Badge> : <Badge tone="neutral">using URL / fallback</Badge>}
            </div>
            <div className="tw-grid tw-gap-4 sm:tw-grid-cols-2">
              <Field label="Twitter / X handle"><Input value={cfg.twitter_site} onChange={(e) => set('twitter_site', e.target.value)} placeholder="@teve" /></Field>
              <Field label="Twitter card type">
                <Select value={cfg.twitter_card_type} onChange={(e) => set('twitter_card_type', e.target.value)}>
                  <option value="summary_large_image">summary_large_image</option>
                  <option value="summary">summary</option>
                </Select>
              </Field>
            </div>
            <Field label="Facebook App ID"><Input value={cfg.facebook_app_id} onChange={(e) => set('facebook_app_id', e.target.value)} /></Field>
          </Section>
          <Section title="Organization (schema.org)">
            <div className="tw-grid tw-gap-4 sm:tw-grid-cols-2">
              <Field label="Name"><Input value={cfg.organization.name} onChange={(e) => set('organization.name', e.target.value)} /></Field>
              <Field label="Legal name"><Input value={cfg.organization.legal_name} onChange={(e) => set('organization.legal_name', e.target.value)} /></Field>
              <Field label="URL"><Input value={cfg.organization.url} onChange={(e) => set('organization.url', e.target.value)} /></Field>
              <Field label="Logo URL"><Input value={cfg.organization.logo} onChange={(e) => set('organization.logo', e.target.value)} /></Field>
              <Field label="Contact email"><Input value={cfg.organization.email} onChange={(e) => set('organization.email', e.target.value)} /></Field>
              <Field label="Contact phone"><Input value={cfg.organization.phone} onChange={(e) => set('organization.phone', e.target.value)} /></Field>
            </div>
            <Field label="Social profile URLs (sameAs)" hint="One per line — LinkedIn, X, YouTube, etc.">
              <Textarea value={(cfg.organization.same_as || []).join('\n')} onChange={(e) => set('organization.same_as', e.target.value.split('\n').map((s) => s.trim()).filter(Boolean))} />
            </Field>
          </Section>
        </>
      )}

      {tab === 'analytics' && (
        <>
          <Section title="Search-engine verification">
            <div className="tw-grid tw-gap-4 sm:tw-grid-cols-2">
              <Field label="Google Search Console"><Input value={cfg.verification.google} onChange={(e) => set('verification.google', e.target.value)} placeholder="content of the meta tag" /></Field>
              <Field label="Bing Webmaster"><Input value={cfg.verification.bing} onChange={(e) => set('verification.bing', e.target.value)} /></Field>
              <Field label="Yandex"><Input value={cfg.verification.yandex} onChange={(e) => set('verification.yandex', e.target.value)} /></Field>
              <Field label="Pinterest"><Input value={cfg.verification.pinterest} onChange={(e) => set('verification.pinterest', e.target.value)} /></Field>
            </div>
          </Section>
          <Section title="Analytics & pixels">
            <div className="tw-grid tw-gap-4 sm:tw-grid-cols-2">
              <Field label="GA4 Measurement ID"><Input value={cfg.analytics.ga4_id} onChange={(e) => set('analytics.ga4_id', e.target.value)} placeholder="G-XXXXXXX" /></Field>
              <Field label="Google Tag Manager ID"><Input value={cfg.analytics.gtm_id} onChange={(e) => set('analytics.gtm_id', e.target.value)} placeholder="GTM-XXXXXX" /></Field>
              <Field label="Meta Pixel ID"><Input value={cfg.analytics.meta_pixel_id} onChange={(e) => set('analytics.meta_pixel_id', e.target.value)} /></Field>
              <Field label="Plausible domain"><Input value={cfg.analytics.plausible_domain} onChange={(e) => set('analytics.plausible_domain', e.target.value)} placeholder="teve.ca" /></Field>
            </div>
            <p className="tw-text-xs tw-text-content-faint">Loaded by the front-end on first paint; page views are sent on every route change.</p>
          </Section>
        </>
      )}

      {tab === 'ai' && (
        <>
          <Section title="AI answer summary (GEO)">
            <Field label="Canonical description for LLMs / answer engines" hint="A crisp, quotable paragraph. Used in llms.txt and returned as ai_summary — this is what ChatGPT, Claude, Perplexity, and Gemini will paraphrase.">
              <Textarea rows={5} value={cfg.ai_summary} onChange={(e) => set('ai_summary', e.target.value)} />
            </Field>
            <div className="tw-flex tw-items-center tw-justify-between">
              <div><div className="tw-font-semibold tw-text-heading">Speakable schema</div><div className="tw-text-sm tw-text-content-muted">Marks h1 / .seo-answer for voice assistants when a page has FAQs</div></div>
              <Toggle checked={!!cfg.speakable_enabled} onChange={(v) => set('speakable_enabled', v ? 1 : 0)} />
            </div>
          </Section>
          <Section title="AI crawler access (robots.txt)">
            <p className="tw-text-sm tw-text-content-muted">Allow the engines you want citing Teve; block the ones you don't. Written into <code>/robots.txt</code>.</p>
            <div className="tw-divide-y tw-divide-line tw-rounded-lg tw-border tw-border-line">
              {CRAWLERS.map(([id, who]) => (
                <div key={id} className="tw-flex tw-items-center tw-justify-between tw-px-3 tw-py-2.5">
                  <div><code className="tw-text-sm tw-text-heading">{id}</code><span className="tw-ml-2 tw-text-xs tw-text-content-faint">{who}</span></div>
                  <Toggle
                    checked={(cfg.ai_crawlers?.[id] ?? 'allow') !== 'disallow'}
                    onChange={(v) => set(`ai_crawlers.${id}`, v ? 'allow' : 'disallow')}
                  />
                </div>
              ))}
            </div>
          </Section>
          <Section title="llms.txt override">
            <Field label="Custom llms.txt" hint="Leave blank to auto-generate from the AI summary + Pages + FAQs. Fill in to serve verbatim at /llms.txt.">
              <Textarea rows={8} value={cfg.llms_txt} onChange={(e) => set('llms_txt', e.target.value)} placeholder={'# Teve\n\n> One-line summary\n\n## Pages\n- [Pricing](https://app.teve.ca/pricing): ...'} />
            </Field>
          </Section>
        </>
      )}

      {tab === 'pages' && (
        <Card
          title="Per-route metadata"
          actions={<Button size="sm" icon="fa-plus" onClick={() => setPageModal({ ...emptyPage })}>Add route</Button>}
          noPad
        >
          {pages.length === 0 ? (
            <EmptyState icon="fa-file-lines" title="No route overrides" hint="Routes without an override use the General defaults." action={<Button icon="fa-plus" onClick={() => setPageModal({ ...emptyPage })}>Add route</Button>} />
          ) : (
            <Table head={['Path', 'Title', 'Robots', 'Sitemap', '']}>
              {pages.map((p) => (
                <Tr key={p.id}>
                  <Td className="tw-font-mono tw-text-xs">{p.path}</Td>
                  <Td className="tw-max-w-[240px] tw-truncate">{p.title || <span className="tw-text-content-faint">— default —</span>}</Td>
                  <Td>{/noindex/.test(p.robots || '') ? <Badge tone="warn">noindex</Badge> : <Badge tone="good">index</Badge>}</Td>
                  <Td>{p.in_sitemap ? <i className="fa-solid fa-check tw-text-good" /> : <i className="fa-solid fa-minus tw-text-content-faint" />}</Td>
                  <Td className="tw-text-right tw-whitespace-nowrap">
                    <Button variant="ghost" size="sm" icon="fa-pen" onClick={() => setPageModal({ ...emptyPage, ...p })}>Edit</Button>
                    <Button variant="ghost" size="sm" icon="fa-trash" onClick={() => removePage(p)} />
                  </Td>
                </Tr>
              ))}
            </Table>
          )}
        </Card>
      )}

      {tab === 'faqs' && (
        <Card
          title="FAQ / Q&A (Answer Engine Optimization)"
          subtitle="Rendered as FAQPage + speakable schema. Scope to a route or leave site-wide."
          actions={<Button size="sm" icon="fa-plus" onClick={() => setFaqModal({ ...emptyFaq })}>Add FAQ</Button>}
          noPad
        >
          {faqs.length === 0 ? (
            <EmptyState icon="fa-circle-question" title="No FAQs yet" hint="Answer-engine results pull heavily from FAQ schema." action={<Button icon="fa-plus" onClick={() => setFaqModal({ ...emptyFaq })}>Add FAQ</Button>} />
          ) : (
            <Table head={['Question', 'Scope', 'Order', '']}>
              {faqs.map((f) => (
                <Tr key={f.id}>
                  <Td className="tw-max-w-[360px] tw-truncate">{f.question}</Td>
                  <Td>{f.page_path ? <code className="tw-text-xs">{f.page_path}</code> : <Badge tone="neutral">site-wide</Badge>}</Td>
                  <Td>{f.sort_order}</Td>
                  <Td className="tw-text-right tw-whitespace-nowrap">
                    <Button variant="ghost" size="sm" icon="fa-pen" onClick={() => setFaqModal({ ...emptyFaq, ...f, page_path: f.page_path || '' })}>Edit</Button>
                    <Button variant="ghost" size="sm" icon="fa-trash" onClick={() => removeFaq(f)} />
                  </Td>
                </Tr>
              ))}
            </Table>
          )}
        </Card>
      )}

      {/* Page editor */}
      <Modal show={!!pageModal} onHide={() => setPageModal(null)} centered size="lg" scrollable>
        <Modal.Header closeButton><Modal.Title>{pageModal?.id ? 'Edit route' : 'Add route'}</Modal.Title></Modal.Header>
        <Modal.Body>
          {pageModal && (
            <div className="tw-space-y-4">
              <Field label="Route path" required hint="e.g. /pricing, /features/live"><Input value={pageModal.path} onChange={(e) => setPageModal((p) => ({ ...p, path: e.target.value }))} placeholder="/pricing" /></Field>
              <Field label="Title"><Input value={pageModal.title} onChange={(e) => setPageModal((p) => ({ ...p, title: e.target.value }))} /></Field>
              <Field label="Description"><Textarea value={pageModal.description} onChange={(e) => setPageModal((p) => ({ ...p, description: e.target.value }))} /></Field>
              <Field label="Keywords"><Input value={pageModal.keywords} onChange={(e) => setPageModal((p) => ({ ...p, keywords: e.target.value }))} /></Field>
              <div className="tw-grid tw-gap-4 sm:tw-grid-cols-2">
                <Field label="OG title (optional)"><Input value={pageModal.og_title} onChange={(e) => setPageModal((p) => ({ ...p, og_title: e.target.value }))} /></Field>
                <Field label="OG image URL (optional)"><Input value={pageModal.og_image} onChange={(e) => setPageModal((p) => ({ ...p, og_image: e.target.value }))} /></Field>
              </div>
              <Field label="OG description (optional)"><Textarea rows={2} value={pageModal.og_description} onChange={(e) => setPageModal((p) => ({ ...p, og_description: e.target.value }))} /></Field>
              <Field label="Canonical URL (optional)" hint="Defaults to base URL + path"><Input value={pageModal.canonical} onChange={(e) => setPageModal((p) => ({ ...p, canonical: e.target.value }))} /></Field>
              <div className="tw-grid tw-gap-4 sm:tw-grid-cols-3">
                <Field label="Robots">
                  <Select value={pageModal.robots} onChange={(e) => setPageModal((p) => ({ ...p, robots: e.target.value }))}>
                    <option value="index,follow">index, follow</option>
                    <option value="noindex,follow">noindex, follow</option>
                    <option value="noindex,nofollow">noindex, nofollow</option>
                    <option value="index,nofollow">index, nofollow</option>
                  </Select>
                </Field>
                <Field label="Change frequency">
                  <Select value={pageModal.changefreq} onChange={(e) => setPageModal((p) => ({ ...p, changefreq: e.target.value }))}>
                    {['always', 'hourly', 'daily', 'weekly', 'monthly', 'yearly', 'never'].map((v) => <option key={v} value={v}>{v}</option>)}
                  </Select>
                </Field>
                <Field label="Sitemap priority"><Input type="number" min="0" max="1" step="0.1" value={pageModal.priority} onChange={(e) => setPageModal((p) => ({ ...p, priority: parseFloat(e.target.value) }))} /></Field>
              </div>
              <div className="tw-flex tw-gap-6">
                <Toggle label="Include in sitemap" checked={!!pageModal.in_sitemap} onChange={(v) => setPageModal((p) => ({ ...p, in_sitemap: v ? 1 : 0 }))} />
                <Toggle label="Active" checked={!!pageModal.is_active} onChange={(v) => setPageModal((p) => ({ ...p, is_active: v ? 1 : 0 }))} />
              </div>
              <Field label="AI summary (GEO, optional)" hint="Overrides the global summary for this route in llms.txt / ai_summary"><Textarea rows={2} value={pageModal.ai_summary} onChange={(e) => setPageModal((p) => ({ ...p, ai_summary: e.target.value }))} /></Field>
              <Field label="Extra JSON-LD (optional)" hint='A JSON object or array — e.g. a Product, Article, or BreadcrumbList block'>
                <Textarea rows={4} className="tw-font-mono tw-text-xs" value={pageModal.json_ld} onChange={(e) => setPageModal((p) => ({ ...p, json_ld: e.target.value }))} placeholder='{"@context":"https://schema.org","@type":"Article","headline":"..."}' />
              </Field>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setPageModal(null)}>Cancel</Button>
          <Button onClick={savePage} disabled={!pageModal?.path}>Save route</Button>
        </Modal.Footer>
      </Modal>

      {/* FAQ editor */}
      <Modal show={!!faqModal} onHide={() => setFaqModal(null)} centered>
        <Modal.Header closeButton><Modal.Title>{faqModal?.id ? 'Edit FAQ' : 'Add FAQ'}</Modal.Title></Modal.Header>
        <Modal.Body>
          {faqModal && (
            <div className="tw-space-y-4">
              <Field label="Question" required><Input value={faqModal.question} onChange={(e) => setFaqModal((f) => ({ ...f, question: e.target.value }))} /></Field>
              <Field label="Answer" required hint="Keep it self-contained — answer engines quote this directly"><Textarea rows={4} value={faqModal.answer} onChange={(e) => setFaqModal((f) => ({ ...f, answer: e.target.value }))} /></Field>
              <div className="tw-grid tw-gap-4 sm:tw-grid-cols-2">
                <Field label="Route scope" hint="Blank = site-wide"><Input value={faqModal.page_path} onChange={(e) => setFaqModal((f) => ({ ...f, page_path: e.target.value }))} placeholder="/pricing" /></Field>
                <Field label="Sort order"><Input type="number" value={faqModal.sort_order} onChange={(e) => setFaqModal((f) => ({ ...f, sort_order: parseInt(e.target.value, 10) || 0 }))} /></Field>
              </div>
              <Toggle label="Active" checked={!!faqModal.is_active} onChange={(v) => setFaqModal((f) => ({ ...f, is_active: v ? 1 : 0 }))} />
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setFaqModal(null)}>Cancel</Button>
          <Button onClick={saveFaq} disabled={!faqModal?.question || !faqModal?.answer}>Save FAQ</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default SeoSettings;
